/**
 * Formats a Date object into a 'YYYY-MM-DD' string in UTC.
 */
export function formatDate(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(
    date.getUTCDate()
  ).padStart(2, '0')}`
}

/**
 * Gets a 'YYYY-MM' string from a Date object in UTC.
 */
export function getYearMonthString(date: Date): string {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`
}

/**
 * Gets the first day of the month for a given date in UTC.
 */
export function getFirstDayOfUTCMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1))
}

/**
 * Calculates the number of calendar days between two dates, inclusive.
 */
export function getDaysInPeriod(startDate: Date, endDate: Date): number {
  if (startDate > endDate) return 0

  const startUTC = Date.UTC(
    startDate.getUTCFullYear(),
    startDate.getUTCMonth(),
    startDate.getUTCDate()
  )
  const endUTC = Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate())

  const oneDay = 1000 * 60 * 60 * 24
  const diffDays = Math.round((endUTC - startUTC) / oneDay) + 1
  return diffDays > 0 ? diffDays : 0
}

/**
 * Creates initials from first and last names.
 */
export function getInitials(firstName = '', lastName = ''): string {
  const firstInitial = firstName?.charAt(0) ?? ''
  const lastInitial = lastName?.charAt(0) ?? ''
  return (firstInitial + lastInitial).toUpperCase()
}

export function getUpdatedEffectiveFromDate(
  salaryEffectiveFrom: string, // The actual effectiveFrom of the salary structure
  employeeJoiningDate: string | null | undefined | Date,
  payPeriodStartDateStr: string
): string {
  if (!employeeJoiningDate) {
    return salaryEffectiveFrom // Fallback if no joining date
  }

  const joiningDate = new Date(employeeJoiningDate + 'T00:00:00Z')
  const payPeriodStartDate = new Date(payPeriodStartDateStr + 'T00:00:00Z')
  const firstDayOfPayrollMonth = getFirstDayOfUTCMonth(payPeriodStartDate) // e.g., May 1st

  // If joined in a previous month compared to payroll month
  if (
    joiningDate.getUTCFullYear() < firstDayOfPayrollMonth.getUTCFullYear() ||
    (joiningDate.getUTCFullYear() === firstDayOfPayrollMonth.getUTCFullYear() &&
      joiningDate.getUTCMonth() < firstDayOfPayrollMonth.getUTCMonth())
  ) {
    return formatDate(firstDayOfPayrollMonth) // Show 1st of current payroll month
  } else {
    // Joined in the current payroll month or later (later shouldn't happen for active payroll)
    // Show the actual salary effective date or joining date if salary effective date is earlier for some reason
    const actualSalaryEffDate = new Date(salaryEffectiveFrom + 'T00:00:00Z')
    if (actualSalaryEffDate < joiningDate) {
      // Should not happen if data is clean
      return formatDate(joiningDate)
    }
    return salaryEffectiveFrom
  }
}
